import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ApiError } from './api/client';
import { LocaleProvider } from './lib/i18n';
import { initialLocale, setActiveLocale } from './lib/i18n/locale';
import { registerServiceWorker } from './lib/registerServiceWorker';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Nothing here changes behind the reader's back, so returning to the tab
      // is not a reason to ask the server anything again.
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      // One retry, but never for a refusal the server meant. A 401 on
      // `GET /api/auth/me` is the ordinary answer for somebody who has not signed
      // in yet, and asking again cannot change it — it only delays the redirect to
      // the sign-in screen by a round trip. Retrying is for the network, not for
      // an answer.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

registerServiceWorker();

// Before the first render, and therefore before the first request: `api/client.ts`
// announces this language with `Accept-Language`, which is how the server knows
// which language to write its refusals in.
setActiveLocale(initialLocale());

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </LocaleProvider>
    </QueryClientProvider>
  </StrictMode>,
);
